import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom';
import { ISideNav } from '../../models/services/ISideNav';
import { AppDispatch, useSelector } from '../../store/AppStore';
import {
  serviceExceptionsComplete,
  serviceExceptionsIncomplete,
  serviceOperationsComplete,
  serviceOperationsIncomplete,
  serviceOperationsIncorrect
} from '../../store/reducer/services/NavBarReducer';
import {
  checkValidationOnServiceEvents,
  checkValidationOnServiceType
} from '../../utilities/services/ServiceDetailsUtils';
import {
  getModeFromUrl,
  getSideNavData,
  getVersionFromUrl,
  updateNavBarStatus
} from '../../utilities/services/ServiceListUtils';
import { MyDropzone } from '../drag-and-drop/DragAndDrop';
import classes from './CreateServiceSideNav.module.scss';

interface CreateServiceSideNavProps {
  viewStatus: boolean;
  isEdit: boolean;
  serviceNameFromURL: string;
  updateFileData: any;
  returnedStatusOpen?: boolean;
}

const CustomNavLink = (props: NavLinkProps) => {
  const { to, className, ...rest } = props;
  const location = useLocation();
  const isActive = !!location.pathname.includes(to.toString());
  const combinedClassName = isActive ? `${className}` : '';

  return <NavLink to={to} className={combinedClassName} {...rest} />;
};

const CreateServiceSideNav = (props: CreateServiceSideNavProps) => {
  const { t: translate } = useTranslation();
  const { viewStatus, serviceNameFromURL, updateFileData, returnedStatusOpen = undefined, isEdit } = props;
  const { serviceDetails: activeServiceDetailsArray } = useSelector((state) => state.getServiceNameReducer);
  const status = activeServiceDetailsArray[0]?.status ?? '';
  const serviceMode = getModeFromUrl();
  const serviceVersion = getVersionFromUrl();
  const location = useLocation();
  const queryParam = location.state?.path;
  const cloneServiceName = location.state?.cloneServiceName ?? '';
  const registryVersion = location.state?.version ?? '';
  const [swaggerShow, setSwaggerShow] = useState(false);
  const { serviceOperations } = useSelector(
    (state) => state.serviceOperationReducer
  );
  const { serviceType } = useSelector((state) => state.serviceDetailsReducer);
  const { ingressEventMappingList } = useSelector(
    (state) => state.ingressEventMappingReducer
  );
  const { egressEventMappingList } = useSelector(
    (state) => state.egressEventMappingReducer
  );
  const {
    serviceDetails,
    serviceExceptions,
    serviceOperations: navBarServiceOperation
  } = useSelector((state) => state.navBarStateReducer);
  const { exceptions } = useSelector((state) => state.exceptionReducer);

  const dispatch = useDispatch<AppDispatch>();

  const [sideBarData, setSideBarData] = useState<ISideNav>(
    getSideNavData(serviceNameFromURL, serviceMode, viewStatus, serviceVersion, translate)
  );

  useEffect(() => {
    let newSideBarData = getSideNavData(serviceNameFromURL, serviceMode, viewStatus, serviceVersion, translate);
    newSideBarData = updateNavBarStatus(
      {
        serviceDetails,
        serviceOperations: navBarServiceOperation,
        serviceExceptions
      },
      newSideBarData
    );
    /* Update the side bar data if any one of the dependancy got change */
    setSideBarData(newSideBarData);
  }, [serviceDetails, navBarServiceOperation, serviceExceptions, serviceType, viewStatus, serviceNameFromURL, status, location.pathname, translate]);

  useEffect(() => {
    /* Based on exception list, update the nav bar state */
    if (exceptions?.length) {
      dispatch(serviceExceptionsComplete());
    } else {
      dispatch(serviceExceptionsIncomplete());
    }
  }, [exceptions]);

  useEffect(() => {
    /* Based on Service Operation list, update the nav bar state */
    if (!serviceOperations?.length) {
      dispatch(serviceOperationsIncomplete());
    } else if (
      !checkValidationOnServiceType(serviceType, serviceOperations)
      || !checkValidationOnServiceEvents(
        serviceType,
        serviceOperations,
        ingressEventMappingList,
        egressEventMappingList
      )
    ) {
      dispatch(serviceOperationsIncorrect());
    } else {
      dispatch(serviceOperationsComplete());
    }
  }, [
    serviceOperations,
    serviceType,
    ingressEventMappingList,
    egressEventMappingList
  ]);

  useEffect(() => {
    if (!isEdit && !viewStatus) setSwaggerShow(true);
    else setSwaggerShow(false);
  }, [isEdit, viewStatus]);

  return (
    <div className={returnedStatusOpen ? classes['side-nav-wrapper-open'] : classes['side-nav-wrapper']}>
      <nav className={classes['cp-side-nav']}>
        <ul className={classes['cp-side-nav-items']}>
          {Object.keys(sideBarData).map((key) => (
            <li
              key={sideBarData[key as keyof ISideNav].path}
              className={
                sideBarData[key as keyof ISideNav].isEnabled
                  ? classes['nav-text']
                  : classes['nav-text-disabled']
              }
            >

              <CustomNavLink
                to={`/control-plane/services${sideBarData[key as keyof ISideNav].path}`}
                state={{ ...location.state, path: queryParam, cloneServiceName, version: registryVersion }}
                className={classes['active-link']}
              >
                <div className={classes.content}>
                  <span className={classes.title}>
                    <span aria-disabled className={classes['title-item']} data-testid={`side-nav-${sideBarData[key as keyof ISideNav].title}`}>
                      {sideBarData[key as keyof ISideNav].title}
                    </span>
                    {!viewStatus && (
                      <div className={sideBarData[key as keyof ISideNav].isEnabled
                        ? classes['nav-icon']
                        : classes['nav-icon-disabled']}
                      >
                        {sideBarData[key as keyof ISideNav].icon}
                      </div>
                    )}
                  </span>
                </div>
              </CustomNavLink>

            </li>
          ))}
        </ul>
      </nav>
      {swaggerShow && !isEdit && !viewStatus && <MyDropzone updateFileData={updateFileData} />}
    </div>
  );
};

export default CreateServiceSideNav;
